/**
 * m3 echo Frontend - Wiki Content
 *
 * Strukturierte Wiki-Inhalte für das m3 echo-Produkt.
 * Alle Texte in deutscher Sprache.
 */

export interface WikiSection {
  id: string;
  title: string;
  content: string;
  children?: WikiSection[];
}

export const wikiSections: WikiSection[] = [
  // ───────────────────────────────────────────────
  // 1. Produktübersicht
  // ───────────────────────────────────────────────
  {
    id: 'overview',
    title: 'Produktübersicht',
    content: `
# Produktübersicht

## Was ist m3 echo?

**m3 echo** ist ein spezialisiertes Tool zur Messung und Optimierung der Sichtbarkeit von Unternehmen in Antworten großer KI-Modelle wie **Google Gemini** und **ChatGPT**. In einer Zeit, in der immer mehr Nutzer direkte Antworten von KI-Assistenten statt klassischer Suchmaschinen verwenden, wird die Präsenz in KI-generierten Antworten zu einem entscheidenden Wettbewerbsfaktor.

m3 echo ermöglicht es Unternehmen, systematisch zu prüfen, **ob und wie sie in KI-Antworten erwähnt werden**, und bietet konkrete Handlungsempfehlungen zur Verbesserung der AI-Visibility.

## Kernfunktionen

### LLM Score Testing

Der LLM Score misst, wie prominent Ihr Unternehmen in den Antworten großer Sprachmodelle (LLMs) erscheint. Das System sendet vordefinierte Prompts an verschiedene KI-Modelle und analysiert die Antworten:

- **Position Ihres Unternehmens** in der Antwort (je weiter oben, desto besser)
- **Häufigkeit der Erwähnung** über verschiedene Prompts hinweg
- **Sentiment-Analyse** (positiv, neutral, negativ) der Erwähnungen
- **Kontextuelle Einordnung** — in welchem Zusammenhang wird Ihr Unternehmen genannt?

Der Score wird auf einer Skala von **0 bis 100** berechnet und ermöglicht den direkten Vergleich über verschiedene Modelle und Zeiträume hinweg.

### Optimierungsanalyse (7 AI-Visibility-Kategorien)

Die Optimierungsanalyse geht über die reine Score-Messung hinaus und untersucht Ihre digitale Präsenz in **sieben spezialisierten Kategorien**:

| Kategorie | Gewichtung | Beschreibung |
|---|---|---|
| **K1 — Entity-Klarheit** | 20 % | Wie eindeutig und konsistent ist Ihr Unternehmen im Web definiert? |
| **K2 — Zitierfähigkeit** | 20 % | Wie gut können KI-Modelle Informationen über Sie zitieren? |
| **K3 — Reputation** | 20 % | Wie wird Ihr Unternehmen in Bewertungen und Medien wahrgenommen? |
| **K4 — USP (Alleinstellungsmerkmale)** | 12 % | Wie klar sind Ihre Alleinstellungsmerkmale für KI erkennbar? |
| **K5 — Lokale Relevanz** | 10 % | Wie stark ist Ihre lokale Präsenz und Auffindbarkeit? |
| **K6 — Drittquellen** | 13 % | Wie häufig werden Sie auf externen Plattformen referenziert? |
| **K7 — Technische Signale** | 5 % | Sind technische Grundlagen (Schema.org, Meta-Tags etc.) vorhanden? |

Jede Kategorie enthält mehrere Sub-Signale, die einzeln bewertet und zu einem Gesamt-Optimierungsscore zusammengefasst werden.

## Warum AI-Visibility wichtig ist

Klassische SEO (Search Engine Optimization) optimiert Webseiten für Suchmaschinen-Rankings. **AI-Visibility** geht einen Schritt weiter: Es geht darum, wie KI-Modelle Ihr Unternehmen in ihren Antworten repräsentieren — unabhängig von klassischen Suchergebnissen. Da KI-Assistenten zunehmend als erste Anlaufstelle für Informationssuche genutzt werden, beeinflusst Ihre AI-Visibility direkt:

- **Markenwahrnehmung** bei potenziellen Kunden
- **Empfehlungswahrscheinlichkeit** durch KI-Assistenten
- **Wettbewerbspositionierung** in KI-generierten Vergleichen
- **Lead-Generierung** über KI-gestützte Recherchen
`,
  },

  // ───────────────────────────────────────────────
  // 2. Grundkonzepte
  // ───────────────────────────────────────────────
  {
    id: 'concepts',
    title: 'Grundkonzepte',
    content: `
# Grundkonzepte

## Projekte

Ein **Projekt** ist der zentrale Container für alle Tests und Analysen zu einem bestimmten Unternehmen oder einer Marke. Jedes Projekt enthält:

- **Name**: Bezeichnung des Projekts (z. B. „Stadtwerke München")
- **Beschreibung**: Optionale Erläuterung zum Projekt
- **Suchbegriff**: Der primäre Begriff, nach dem in KI-Antworten gesucht wird (z. B. „Stadtwerke München")
- **2 Synonyme**: Alternative Bezeichnungen, die ebenfalls als Treffer gewertet werden (z. B. „SWM", „Münchner Stadtwerke")

Projekte bündeln alle zugehörigen Tests, Ergebnisse und Optimierungsanalysen an einem Ort.

## Prompts

**Prompts** sind die Fragen, die an KI-Modelle gesendet werden. Sie sind das Herzstück jedes Tests und bestimmen, in welchem Kontext die Sichtbarkeit geprüft wird.

Prompts unterstützen **Platzhalter** in eckigen Klammern, die bei der Testausführung dynamisch ersetzt werden:

- \`[Ort]\` — wird durch den Standort des Unternehmens ersetzt
- \`[Branche]\` — wird durch die Branche ersetzt
- \`[Produkt]\` — wird durch ein spezifisches Produkt/Dienstleistung ersetzt

**Beispiel:** „Welche Unternehmen in \`[Ort]\` bieten die besten \`[Produkt]\`-Lösungen an?"

Prompts werden entweder global über die **Einstellungen** verwaltet oder direkt bei der **Projekterstellung** definiert.

## Jobs

Ein **Job** ist eine einzelne Testausführung, bei der alle konfigurierten Prompts an die zentral festgelegten KI-Modelle gesendet werden. Es gibt zwei Typen:

- **LLM Score Jobs**: Messen die Position und Sichtbarkeit in KI-Antworten
- **Optimierungs-Jobs**: Analysieren die digitale Präsenz anhand der 7 Kategorien

Jeder Job durchläuft folgende Status:

| Status | Bedeutung |
|---|---|
| **Pending** | Job wartet in der Warteschlange auf Ausführung |
| **Running** | Job wird gerade ausgeführt, Prompts werden an Modelle gesendet |
| **Completed** | Job wurde erfolgreich abgeschlossen |
| **Failed** | Job ist fehlgeschlagen (Details in der Fehlermeldung) |

## LLM Score

Der **LLM Score** ist eine numerische Bewertung (0–100), die die Sichtbarkeit Ihres Unternehmens in einer KI-Antwort widerspiegelt. Die Berechnung basiert auf der **Position** der Erwähnung:

| Position | Score |
|---|---|
| Position 1 (erste Erwähnung) | **100** |
| Position 2 | **80** |
| Position 3 | **60** |
| Position 4 oder höher | **30** |
| Nicht erwähnt | **0** |

Der Gesamtscore eines Tests wird so gemittelt, dass **jeder Anbieter gleich zählt**, egal wie viele seiner Modelle in der Aufstellung sind:

1. Je Anbieter und Prompt der Mittelwert über die Modelle dieses Anbieters
2. Je Anbieter der Mittelwert über seine Prompts
3. Der Mittelwert über die Anbieter

Beispiel: Zwei Gemini-Modelle erreichen 100 und 60 Punkte, ein GPT-Modell 0 Punkte. Google kommt auf 80, OpenAI auf 0, der Score ist **40** (nicht 53,3 wie beim einfachen Durchschnitt aller drei Ergebnisse).

**Fehlgeschlagene Ergebnisse zählen nicht**: Ein API-Fehler, ein Timeout oder ein nicht verfügbares Modell wird nicht als 0 gewertet, sondern bleibt außen vor. Ein Anbieter, dessen Ergebnisse alle fehlschlagen, fällt aus dem Mittel heraus. Hat ein Test gar kein gültiges Ergebnis, hat er keinen Score und wird als **„nicht auswertbar“** angezeigt.

Die **Nennungsrate** eines Tests folgt derselben Regel (genannt = 1, nicht genannt = 0).

## Optimierungs-Tests

Optimierungs-Tests bewerten Ihre digitale Präsenz anhand von **7 AI-Visibility-Kategorien** mit unterschiedlicher Gewichtung:

| Kategorie | Kürzel | Gewichtung |
|---|---|---|
| Entity-Klarheit | K1 | 20 % |
| Zitierfähigkeit | K2 | 20 % |
| Reputation | K3 | 20 % |
| USP (Alleinstellungsmerkmale) | K4 | 12 % |
| Lokale Relevanz | K5 | 10 % |
| Drittquellen | K6 | 13 % |
| Technische Signale | K7 | 5 % |

### Bewertungsskala

Der resultierende Optimierungsscore wird nach folgenden Labels eingestuft:

| Score-Bereich | Label | Bedeutung |
|---|---|---|
| 90–100 | **Exzellent** | Hervorragende AI-Visibility, kaum Optimierungsbedarf |
| 75–89 | **Gut** | Solide Basis, gezielte Verbesserungen möglich |
| 60–74 | **Ausbaufähig** | Deutliches Verbesserungspotenzial in mehreren Bereichen |
| 40–59 | **Schwach** | Erheblicher Handlungsbedarf, viele Signale fehlen |
| 0–39 | **Kritisch** | Dringende Maßnahmen erforderlich, kaum AI-Visibility |

## Combined Timeline

Die **Combined Timeline** vereint LLM Score und Optimierungsscore in einer gemeinsamen Zeitlinie. Dies ermöglicht:

- **Trendanalyse**: Wie entwickeln sich beide Metriken über die Zeit?
- **Korrelationsanalyse**: Führen Optimierungsmaßnahmen zu besseren LLM Scores?

Der **Pearson-Korrelationskoeffizient (r)** quantifiziert den linearen Zusammenhang zwischen LLM Score und Optimierungsscore:

| Wert von r | Interpretation |
|---|---|
| r nahe +1,0 | Starke positive Korrelation — Optimierung und Score steigen gemeinsam |
| r nahe 0 | Kein linearer Zusammenhang erkennbar |
| r nahe −1,0 | Gegenläufige Entwicklung |

Ein hoher positiver r-Wert bestätigt, dass die Optimierungsmaßnahmen tatsächlich Wirkung zeigen.
`,
  },

  // ───────────────────────────────────────────────
  // 3. Dashboard
  // ───────────────────────────────────────────────
  {
    id: 'dashboard',
    title: 'Dashboard',
    content: `
# Dashboard

## Übersicht

Das **Dashboard** ist die zentrale Startseite von m3 echo und bietet einen schnellen Überblick über alle Projekte. Die Darstellung ist als Tabelle (Desktop) bzw. als Karten-Ansicht (Mobile) optimiert.

## Projekt-Tabelle

Jedes Projekt wird mit folgenden Informationen angezeigt:

| Spalte | Beschreibung |
|---|---|
| **ID** | Eindeutige Projekt-Nummer |
| **Name** | Projektname (klickbar für Details) |
| **Suchbegriff** | Der primäre Suchbegriff des Projekts |
| **Tests** | Anzahl durchgeführter Tests |
| **Durchschnitt** | Durchschnittlicher LLM Score der letzten 10 bewerteten Tests (nicht auswertbare Tests zählen nicht) |
| **Letzter Test** | Datum des letzten durchgeführten Tests |
| **Trend** | Tendenz-Indikator: ↑ (steigend), ↓ (fallend), → (stabil); jede Veränderung gegenüber dem vorherigen Test zählt. Fehlt einem der beiden Tests der Score, wird kein Trend angezeigt |

## Filter-Funktion

Über das **Suchfeld** oberhalb der Tabelle können Projekte nach Name oder Suchbegriff gefiltert werden. Die Filterung erfolgt in Echtzeit während der Eingabe.

## Bulk-Operationen

Durch Auswahl mehrerer Projekte (Checkboxen) stehen folgende Aktionen zur Verfügung:

- **LLM Score Test starten**: Führt einen LLM Score Test für alle ausgewählten Projekte durch
- **Optimierung starten**: Startet eine Optimierungsanalyse für alle ausgewählten Projekte
- **Löschen**: Entfernt die ausgewählten Projekte nach Bestätigung

Die Bulk-Aktionen erscheinen in einer Aktionsleiste, sobald mindestens ein Projekt ausgewählt ist.

## Projekt erstellen

Über den **„+ Projekt erstellen"**-Button oben rechts gelangt man zum 4-Schritt-Wizard zur Projekterstellung (siehe Benutzerhandbuch → Projekt-Erstellung).

## Autopilot-Anzeige

Projekte mit aktivem Autopilot werden mit einem **🔁-Icon** in der Tabelle gekennzeichnet. So ist sofort ersichtlich, welche Projekte automatisiert getestet werden.
`,
  },

  // ───────────────────────────────────────────────
  // 4. Benutzerhandbuch
  // ───────────────────────────────────────────────
  {
    id: 'user-guide',
    title: 'Benutzerhandbuch',
    content: `
# Benutzerhandbuch

Das Benutzerhandbuch beschreibt alle Funktionen von m3 echo im Detail. Wählen Sie einen der folgenden Bereiche:

- **Projekt-Erstellung** — Schritt-für-Schritt durch den 4-Schritt-Wizard
- **Tests durchführen** — LLM Score und Optimierungstests starten
- **Ergebnisse verstehen** — Statistiken, Diagramme und Heatmaps lesen
- **Optimierung** — Die 7 Kategorien verstehen und Empfehlungen umsetzen
- **Combined Timeline** — Korrelation zwischen Score und Optimierung analysieren
- **Re-Tests** — Vergleichbare Nachtests durchführen
- **Prompts verwalten** — Erstellen, bearbeiten und mit Platzhaltern arbeiten
`,
    children: [
      // 4.1 Projekt-Erstellung
      {
        id: 'project-creation',
        title: 'Projekt-Erstellung',
        content: `
# Projekt-Erstellung

## 4-Schritt-Wizard

Die Projekterstellung führt Sie in vier aufeinanderfolgenden Schritten durch die Konfiguration:

### Schritt 1: Name & Grunddaten

- **Projektname**: Eindeutiger Name für Ihr Projekt (z. B. „Mein Unternehmen GmbH")
- **Beschreibung**: Optionale Beschreibung zur internen Dokumentation
- **Suchbegriff**: Der primäre Begriff, nach dem in KI-Antworten gesucht wird
- **Synonym 1 & 2**: Alternative Bezeichnungen oder Abkürzungen Ihres Unternehmens

> **Tipp:** Wählen Sie als Suchbegriff die geläufigste Bezeichnung Ihres Unternehmens. Die Synonyme sollten gängige Abkürzungen oder alternative Schreibweisen abdecken (z. B. „BMW" und „Bayerische Motoren Werke").

### Schritt 2: Prompts auswählen

Wählen Sie die Prompts aus, die für dieses Projekt verwendet werden sollen:

- **Vorhandene Prompts** werden als Liste angezeigt und können per Checkbox ausgewählt werden
- Über **„Neuen Prompt erstellen"** können Sie direkt neue Prompts anlegen
- Es wird empfohlen, mindestens **3–5 verschiedene Prompts** für aussagekräftige Ergebnisse zu verwenden

### Schritt 3: Platzhalter konfigurieren

Falls Ihre Prompts Platzhalter wie \`[Ort]\` oder \`[Branche]\` enthalten, werden diese hier mit konkreten Werten befüllt:

- Jeder im Prompt verwendete Platzhalter wird als Eingabefeld angezeigt
- Tragen Sie den passenden Wert ein (z. B. \`[Ort]\` → „München")
- Die Platzhalter werden bei der Testausführung automatisch ersetzt

### Schritt 4: Zusammenfassung

Überprüfen Sie alle Einstellungen vor dem Erstellen:

- Projektname und Suchbegriff werden angezeigt
- Ausgewählte Prompts und deren Platzhalter-Belegung

Die KI-Modelle werden nicht ausgewählt: Sie legt der Administrator zentral fest.

Nach Klick auf **„Projekt erstellen"** wird das Projekt angelegt und Sie werden zur Projektdetailseite weitergeleitet.
`,
      },

      // 4.2 Tests durchführen
      {
        id: 'running-tests',
        title: 'Tests durchführen',
        content: `
# Tests durchführen

## Testarten

m3 echo bietet zwei Testarten:

- **LLM Score Test**: Misst die Sichtbarkeit in KI-Antworten (Position, Erwähnung, Sentiment)
- **Optimierungs-Test**: Analysiert Ihre digitale Präsenz anhand der 7 AI-Visibility-Kategorien

## Testausführung

### Einen Test starten

1. Navigieren Sie zur **Projektdetailseite**
2. Klicken Sie auf **„LLM Score Test"** oder **„Optimierung starten"**
3. Bestätigen Sie die Testparameter (Prompts)

### Was passiert im Hintergrund?

Die Testausführung erfolgt in zwei Schichten:

**Layer 1 — Prompting:**
- Die konfigurierten Prompts werden an die zentral festgelegten KI-Modelle gesendet
- Platzhalter werden durch die hinterlegten Werte ersetzt
- Die Antworten der Modelle werden gesammelt

**Layer 2 — Analyse:**
- Die gesammelten Antworten werden systematisch ausgewertet
- Position, Sentiment und Kontext Ihres Suchbegriffs werden extrahiert
- Bei Optimierungstests werden zusätzlich die 7 Kategorie-Scores berechnet

### Dauer

Ein typischer Test dauert je nach Anzahl der Prompts und Modelle **30–120 Sekunden**. Während der Ausführung können Sie die Seite verlassen — der Test läuft im Hintergrund weiter und Sie werden über den Abschluss informiert.

## Tests über das Dashboard starten

Alternativ können Tests direkt vom Dashboard aus gestartet werden:

1. Wählen Sie ein oder mehrere Projekte per Checkbox aus
2. Klicken Sie auf **„LLM Score"** oder **„Optimierung"** in der Aktionsleiste
3. Die Tests werden für alle ausgewählten Projekte gleichzeitig gestartet
`,
      },

      // 4.3 Ergebnisse verstehen
      {
        id: 'results',
        title: 'Ergebnisse verstehen',
        content: `
# Ergebnisse verstehen

## Überblick Statistiken

Nach Abschluss eines Tests werden folgende Statistiken auf der Projektdetailseite angezeigt:

- **Anzahl Tests**: Gesamtzahl der durchgeführten Tests
- **Trend**: Vergleich des jüngsten mit dem vorherigen Test (steigend/fallend/stabil). Jede Differenz zählt als Veränderung; fehlt einem der beiden Tests der Score, wird kein Trend angezeigt
- **⌀ Punktzahl**: Mittelwert der letzten 10 bewerteten Tests; der Score jedes Tests ist je Anbieter gemittelt (siehe *LLM Score*)
- **Zuerst genannte Firma**: Die am häufigsten zuerst genannte Firma in den letzten 20 Tests. Marken und Produkte sind technisch eingeschlossen; es ist keine Empfehlung

Tests ohne gültiges Ergebnis haben keinen Score und erscheinen als **„nicht auswertbar“**. In den Diagrammen fehlt für sie der Punkt bzw. Balken; der **7T-Mittelwert** mittelt nur die gültigen Werte einer 7er-Gruppe, der Tooltip nennt deren Anzahl.

## Balkendiagramme

Die Ergebnisse werden als **Balkendiagramme** visualisiert:

- **Score pro Modell**: Vergleich der Sichtbarkeit über verschiedene KI-Modelle
- **Score pro Prompt**: Welche Fragestellungen liefern die besten/schlechtesten Ergebnisse?
- **Farbcodierung**: Grün (gut) → Gelb (mittel) → Orange (schwach) → Rot (kritisch)

## Modell-Details

Für jedes getestete Modell werden detaillierte Ergebnisse angezeigt:

| Feld | Beschreibung |
|---|---|
| **Score** | Berechneter LLM Score (0–100); bei einem fehlgeschlagenen Ergebnis „nicht auswertbar“ statt 0, auch im Prompt-Vergleich und im PDF. Im CSV-Export steht dann in der Spalte „Evaluable“ ein „No“, Score und „Mentioned“ bleiben leer |
| **Position** | An welcher Stelle Ihr Unternehmen in der Antwort erwähnt wird |
| **Sentiment** | Bewertung der Erwähnung: positiv, neutral oder negativ |
| **Kontext** | Der Textauszug, in dem Ihr Unternehmen genannt wird |

## Score Heatmap

Die **Heatmap** bietet eine visuelle Gesamtübersicht aller Prompt-Modell-Kombinationen:

- **Zeilen**: Prompts
- **Spalten**: KI-Modelle
- **Zellen**: Farbcodierter Score

Ohne Modellfilter ist eine Zelle je Anbieter gemittelt wie der Gesamtscore; mit Modellfilter zeigt sie den Score dieses Modells. Eine Zelle ohne gültiges Ergebnis bleibt leer.

### Farbcodierung der Heatmap

| Farbe | Score-Bereich | Bedeutung |
|---|---|---|
| **Grün** | 75–100 | Gute bis exzellente Sichtbarkeit |
| **Gelb** | 50–74 | Mittlere Sichtbarkeit, Verbesserungspotenzial |
| **Orange** | 25–49 | Schwache Sichtbarkeit, Handlungsbedarf |
| **Rot** | 0–24 | Kaum oder keine Sichtbarkeit |

Auf mobilen Geräten wird die Heatmap in einem **ausklappbaren Akkordeon** dargestellt (standardmäßig eingeklappt), um Platz zu sparen.
`,
      },

      // 4.4 Optimierung
      {
        id: 'optimization',
        title: 'Optimierung',
        content: `
# Optimierung

## Homepage-URL konfigurieren

Bevor eine Optimierungsanalyse durchgeführt werden kann, muss die **Homepage-URL** des Unternehmens konfiguriert werden. Diese wird verwendet, um die technischen Signale (K7) und andere web-basierte Faktoren zu analysieren.

Navigieren Sie zur Projektdetailseite und geben Sie die URL in das dafür vorgesehene Feld ein (z. B. \`https://www.mein-unternehmen.de\`).

## Die 7 AI-Visibility-Kategorien im Detail

### K1 — Entity-Klarheit (20 %)

Bewertet, wie eindeutig und konsistent Ihr Unternehmen im Web definiert ist:

- **Knowledge-Graph-Eintrag**: Existiert ein Eintrag bei Google/Wikidata?
- **Einheitliche Namenverwendung**: Wird überall der gleiche Unternehmensname genutzt?
- **Beschreibungskonsistenz**: Stimmen Beschreibungen auf verschiedenen Plattformen überein?
- **Kategorisierung**: Ist Ihr Unternehmen korrekt kategorisiert/branchen-zugeordnet?

### K2 — Zitierfähigkeit (20 %)

Bewertet, wie gut KI-Modelle verlässliche Informationen über Sie finden und zitieren können:

- **Strukturierte Daten**: Sind Schema.org-Markups vorhanden?
- **Quellenvielfalt**: Gibt es mehrere unabhängige, zitierfähige Quellen?
- **Aktualität**: Sind die verfügbaren Informationen aktuell?
- **Faktenbasierte Inhalte**: Gibt es konkrete Zahlen, Fakten und Daten?

### K3 — Reputation (20 %)

Bewertet die Außenwahrnehmung Ihres Unternehmens:

- **Bewertungsportale**: Durchschnittliche Bewertungen auf Google, Trustpilot etc.
- **Medienberichterstattung**: Erwähnungen in Nachrichtenmedien und Fachpresse
- **Social-Media-Präsenz**: Aktivität und Engagement in sozialen Netzwerken
- **Branchenverzeichnisse**: Einträge und Bewertungen in relevanten Verzeichnissen

### K4 — USP / Alleinstellungsmerkmale (12 %)

Bewertet, wie klar Ihre Alleinstellungsmerkmale kommuniziert werden:

- **Wettbewerbsabgrenzung**: Sind Ihre Besonderheiten klar formuliert?
- **Leistungsversprechen**: Werden USPs auf der Website deutlich kommuniziert?
- **Branchenpositionierung**: Sind Sie als Spezialist oder Marktführer erkennbar?

### K5 — Lokale Relevanz (10 %)

Bewertet Ihre Sichtbarkeit im lokalen Kontext:

- **Google Business Profile**: Ist Ihr Profil vollständig und aktuell?
- **Lokale Verzeichnisse**: Sind Sie in relevanten lokalen Verzeichnissen gelistet?
- **NAP-Konsistenz**: Stimmen Name, Adresse und Telefonnummer überall überein?
- **Lokale Keywords**: Werden standortbezogene Begriffe verwendet?

### K6 — Drittquellen (13 %)

Bewertet, wie häufig Sie auf externen Plattformen referenziert werden:

- **Backlink-Profil**: Wie viele qualitative externe Links verweisen auf Sie?
- **Erwähnungen ohne Link**: Werden Sie auch ohne direkten Link erwähnt?
- **Branchenverzeichnisse**: Einträge in relevanten Fach-Verzeichnissen
- **Partner- und Referenzseiten**: Werden Sie von Partnern verlinkt/erwähnt?

### K7 — Technische Signale (5 %)

Bewertet die technischen Grundlagen Ihrer Web-Präsenz:

- **Schema.org-Markup**: Sind strukturierte Daten implementiert? m3 echo liest sie direkt von der Homepage und höchstens einer verlinkten Angebotsseite aus (ohne JavaScript-Ausführung). Was dort nicht steht, gilt als „auf den geprüften Seiten nicht gefunden“; ist die Seite nicht erreichbar, als „nicht prüfbar“
- **Maschinenlesbare Angebote**: Sind Produkte, Preise und Tarife strukturiert ausgezeichnet (Offer mit Preis, Währung, Gültigkeit und Gebiet)?
- **Meta-Tags**: Sind Title und Description optimiert?
- **Open Graph Tags**: Sind Social-Media-Meta-Tags vorhanden?
- **SSL-Zertifikat**: Ist die Seite via HTTPS erreichbar?
- **Ladezeiten**: Wie performant ist Ihre Website?

## Empfehlungen und Wizard

Zu jeder Kategorie liefert m3 echo **konkrete Empfehlungen** zur Verbesserung. Diese umfassen:

- **Priorität**: Hoch, Mittel oder Niedrig
- **Aufwand**: Geschätzter Implementierungsaufwand
- **Beschreibung**: Was genau verbessert werden sollte
- **Erwartete Wirkung**: Welche Score-Verbesserung zu erwarten ist

Über den **Wizard-Button** können Sie für ausgewählte Empfehlungen automatisch **Entwürfe** erstellen lassen. Diese Entwürfe dienen als Ausgangsbasis für die Umsetzung der Optimierungsmaßnahmen.
`,
      },

      // 4.5 Combined Timeline
      {
        id: 'combined-timeline',
        title: 'Combined Timeline',
        content: `
# Combined Timeline

## Überblick

Die **Combined Timeline** ist eine leistungsstarke Analysefunktion, die LLM Score und Optimierungsscore in einer gemeinsamen Zeitlinie darstellt. So können Sie die Entwicklung beider Metriken über die Zeit verfolgen und Zusammenhänge erkennen.

## Darstellung

- **X-Achse**: Zeitverlauf (Datum der Tests)
- **Linke Y-Achse**: LLM Score (0–100)
- **Rechte Y-Achse**: Optimierungsscore (0–100)
- **Linie 1**: LLM Score Verlauf (blau)
- **Linie 2**: Optimierungsscore Verlauf (grün)

## Korrelationskoeffizient (r)

Der **Pearson-Korrelationskoeffizient** wird automatisch berechnet und angezeigt:

- Misst den linearen Zusammenhang zwischen LLM Score und Optimierungsscore
- Wertebereich: **−1,0 bis +1,0**
- Wird prominent über dem Diagramm angezeigt

### Interpretation

| Wert | Stärke | Bedeutung |
|---|---|---|
| **+0,7 bis +1,0** | Stark positiv | Optimierungen wirken sich klar auf den LLM Score aus |
| **+0,4 bis +0,7** | Moderat positiv | Tendenzieller Zusammenhang erkennbar |
| **−0,3 bis +0,3** | Schwach/kein | Kein eindeutiger linearer Zusammenhang |
| **−0,7 bis −0,3** | Moderat negativ | Gegenläufige Tendenz (ungewöhnlich) |
| **−1,0 bis −0,7** | Stark negativ | Starke Gegenläufigkeit (sollte untersucht werden) |

## Smart Grouping

Bei vielen Datenpunkten aktiviert die Combined Timeline automatisch **Smart Grouping**:

- Tests werden nach sinnvollen Zeitintervallen gruppiert (Tag, Woche, Monat)
- Durchschnittswerte pro Gruppierung werden berechnet
- Reduziert visuelle Überladung bei vielen Tests
- Kann manuell ein-/ausgeschaltet werden
`,
      },

      // 4.6 Re-Tests
      {
        id: 'retests',
        title: 'Re-Tests',
        content: `
# Re-Tests

## Zweck

Re-Tests ermöglichen es, einen früheren Test mit **identischen Prompts, Platzhaltern und Suchbegriffen** zu wiederholen. So können Sie die Entwicklung Ihrer Sichtbarkeit über die Zeit vergleichen. Die KI-Modelle stammen aus der aktuellen zentralen Modellaufstellung.

## LLM Score Re-Test

Ein LLM Score Re-Test verwendet die gleichen Inhalte wie der ursprüngliche Test:

- **Gleiche Prompts** mit gleicher Platzhalter-Belegung
- **Aktuelle KI-Modelle** der zentralen Modellaufstellung (bei geänderter Aufstellung können sich diese vom ursprünglichen Test unterscheiden; frühere Ergebnisse bleiben sichtbar)
- **Gleicher Suchbegriff** und Synonyme

So starten Sie einen Re-Test:

1. Öffnen Sie die **Projektdetailseite**
2. Navigieren Sie zum gewünschten abgeschlossenen Test
3. Klicken Sie auf **„Re-Test"**
4. Der Test wird mit denselben Prompts, Platzhaltern und Suchbegriffen und den aktuellen Modellen erneut ausgeführt

## Optimierungs-Re-Test

Auch Optimierungstests können als Re-Test durchgeführt werden:

- **Gleiche Homepage-URL**
- **Gleiche Analyse-Konfiguration**
- Ermöglicht den direkten Vorher/Nachher-Vergleich nach Optimierungsmaßnahmen

## Vergleichbarkeit sicherstellen

Re-Tests sind besonders wertvoll, weil sie die **Vergleichbarkeit** garantieren:

- Keine Verzerrung durch unterschiedliche Prompts, Platzhalter oder Suchbegriffe
- Bei geänderter Modellaufstellung zeigt die Auswertung je Modell, welche Ergebnisse von welchem Modell stammen
- Ideal für die Erfolgsmessung nach Optimierungsmaßnahmen

Der **Re-Test-Vergleich** stellt Ergebnisse paarweise gegenüber (gleiches Modell, gleicher Prompt). Verglichen werden nur Paare, die in beiden Tests ein gültiges Ergebnis haben. Die angezeigte **durchschnittliche Veränderung** ist der Mittelwert über diese Paare – eine eigene Kennzahl, nicht die Differenz der beiden Test-Scores.
`,
      },

      // 4.7 Prompts verwalten
      {
        id: 'prompts',
        title: 'Prompts verwalten',
        content: `
# Prompts verwalten

## Übersicht

Prompts sind die Fragen, die an die KI-Modelle gesendet werden. Eine gute Prompt-Auswahl ist entscheidend für aussagekräftige Testergebnisse.

## Prompt erstellen

Es gibt zwei Wege, neue Prompts zu erstellen:

### Über die Einstellungen

1. Navigieren Sie zu **Einstellungen → Prompts**
2. Klicken Sie auf **„+ Neuer Prompt"**
3. Geben Sie den Prompt-Text ein
4. Optional: Verwenden Sie Platzhalter in eckigen Klammern
5. Speichern Sie den Prompt

### Bei der Projekterstellung

Im **Schritt 2** des Projekt-Wizards können Sie direkt neue Prompts erstellen:

1. Klicken Sie auf **„Neuen Prompt erstellen"**
2. Geben Sie den Text ein
3. Der Prompt wird erstellt und automatisch dem Projekt zugeordnet

## Prompt bearbeiten

1. Navigieren Sie zu **Einstellungen → Prompts**
2. Klicken Sie auf das **Bearbeiten-Symbol** (Stift-Icon) neben dem gewünschten Prompt
3. Passen Sie den Text an
4. Speichern Sie die Änderungen

> **Hinweis:** Änderungen an Prompts wirken sich nicht auf bereits abgeschlossene Tests aus. Diese behalten den ursprünglichen Prompt-Text bei (Prompt Snapshotting).

## Prompt löschen

1. Navigieren Sie zu **Einstellungen → Prompts**
2. Klicken Sie auf das **Löschen-Symbol** (Papierkorb-Icon)
3. Bestätigen Sie die Löschung im Bestätigungsdialog

> **Achtung:** Das Löschen eines Prompts ist endgültig. Bereits durchgeführte Tests sind davon nicht betroffen.

## Platzhalter-Syntax

Platzhalter werden in **eckigen Klammern** geschrieben und bei der Testausführung durch konkrete Werte ersetzt:

| Platzhalter | Beispielwert | Beschreibung |
|---|---|---|
| \`[Ort]\` | München | Standort des Unternehmens |
| \`[Branche]\` | Energieversorgung | Branche des Unternehmens |
| \`[Produkt]\` | Ökostrom | Spezifisches Produkt oder Dienstleistung |
| \`[Region]\` | Oberbayern | Regionale Eingrenzung |

### Beispiele

- „Welche \`[Branche]\`-Unternehmen in \`[Ort]\` sind führend?"
- „Empfehle mir einen Anbieter für \`[Produkt]\` in \`[Region]\`."
- „Was sind die besten \`[Produkt]\`-Anbieter in \`[Ort]\`?"

### Best Practices

- Verwenden Sie **3–5 unterschiedliche Prompts** pro Projekt
- Formulieren Sie Prompts so, wie echte Nutzer KI-Assistenten befragen würden
- Mischen Sie **allgemeine und spezifische** Fragestellungen
- Nutzen Sie Platzhalter für **standort- oder branchenspezifische** Anpassungen
`,
      },
    ],
  },

  // ───────────────────────────────────────────────
  // 5. Autopilot
  // ───────────────────────────────────────────────
  {
    id: 'autopilot',
    title: 'Autopilot',
    content: `
# Autopilot

## Überblick

Der **Autopilot** automatisiert die regelmäßige Durchführung von Tests. Statt manuell Tests zu starten, übernimmt der Autopilot dies nach einem konfigurierbaren Zeitplan.

## Autopilot-Typen

### LLM Score Autopilot

- **Ausführung**: Täglich um **7:00 Uhr** (Europe/Berlin)
- **Funktion**: Führt automatisch einen LLM Score Test mit den konfigurierten Parametern durch
- **Ergebnisse**: Werden wie manuelle Tests in der Projekthistorie gespeichert

### Optimierungs-Autopilot

- **Ausführung**: Wöchentlich an einem **wählbaren Tag**
- **Funktion**: Führt automatisch eine Optimierungsanalyse durch
- **Tag wählen**: Bei der Erstellung wählen Sie den Wochentag (Montag–Sonntag)

## Autopilot erstellen

1. Navigieren Sie zur **Projektdetailseite**
2. Klicken Sie auf **„Autopilot einrichten"**
3. Wählen Sie den Typ (LLM Score oder Optimierung)
4. Bei Optimierung: Wählen Sie den gewünschten Wochentag
5. Bestätigen Sie die Einrichtung

## Autopilot verwalten

### Pausieren / Reaktivieren

- Ein aktiver Autopilot kann jederzeit **pausiert** werden
- Pausierte Autopiloten führen keine Tests durch, bleiben aber konfiguriert
- Ein pausierter Autopilot kann jederzeit wieder **reaktiviert** werden

### Löschen

- Ein Autopilot kann vollständig **gelöscht** werden
- Dies beendet alle zukünftigen automatischen Tests
- Bereits durchgeführte Tests bleiben erhalten

## Prompt-Kontingent

Das Prompt-Kontingent zeigt an, wie viele automatische Prompt-Ausführungen im aktuellen Zeitraum noch verfügbar sind:

| Farbe | Bedeutung |
|---|---|
| **Grün** | Ausreichend Kontingent vorhanden (> 50 %) |
| **Gelb** | Kontingent wird knapp (20–50 %) |
| **Rot** | Kontingent fast aufgebraucht (< 20 %) |

Das Kontingent wird pro Tenant verwaltet und kann vom Administrator angepasst werden.

## Zeitzone

- Alle Autopilot-Zeiten beziehen sich auf die Zeitzone **Europe/Berlin**
- Die **Sommerzeit** (MESZ) wird automatisch berücksichtigt
- Umstellungen im Frühjahr und Herbst werden korrekt gehandhabt

## Sofortausführung

Ein Autopilot kann zusätzlich zum regulären Zeitplan auch **sofort ausgeführt** werden:

- Klicken Sie auf **„Jetzt ausführen"** auf der Projektdetailseite
- Der Test wird umgehend gestartet, der reguläre Zeitplan bleibt davon unberührt

## Sequentielle Ausführung

Wenn mehrere Projekte Autopiloten für den gleichen Zeitpunkt konfiguriert haben, werden die Tests **sequentiell** (nacheinander) ausgeführt. Dies vermeidet Überlastung der KI-Modell-APIs und stellt sicher, dass jeder Test vollständig verarbeitet wird.

## Dashboard-Anzeige

Projekte mit aktivem Autopilot werden im Dashboard mit einem **🔁-Icon** gekennzeichnet. So erkennen Sie auf einen Blick, welche Projekte automatisiert überwacht werden.

## Häufig gestellte Fragen (FAQ)

**Was passiert, wenn ein Autopilot-Test fehlschlägt?**
Der Test wird als „Failed" markiert und beim nächsten regulären Zeitpunkt erneut versucht. Sie können den Fehlerstatus in der Projektdetailseite einsehen.

**Kann ich mehrere Autopiloten pro Projekt haben?**
Ja, Sie können einen LLM Score Autopiloten und einen Optimierungs-Autopiloten gleichzeitig betreiben.

**Wird mein Kontingent verbraucht, wenn ein Test fehlschlägt?**
Nein, fehlgeschlagene Tests werden nicht auf das Prompt-Kontingent angerechnet.

**Wie ändere ich den Wochentag des Optimierungs-Autopiloten?**
Löschen Sie den bestehenden Autopiloten und erstellen Sie einen neuen mit dem gewünschten Tag.
`,
  },

  // ───────────────────────────────────────────────
  // 6. KI-Modelle
  // ───────────────────────────────────────────────
  {
    id: 'models',
    title: 'KI-Modelle',
    content: `
# KI-Modelle

## Verfügbare Modelle

m3 echo unterstützt Modelle von **Google Gemini** und **OpenAI GPT** für LLM Score Tests. Die Modelle legt der Administrator zentral fest; neue Modelle werden nach Prüfung für alle Projekte freigegeben. Ergebnisse früherer Modelle bleiben in der Auswertung sichtbar.

Sie müssen bei der Projekterstellung, bei Tests oder bei Autopiloten kein Modell auswählen. Autopiloten, manuelle Tests und Re-Tests verwenden ab dem nächsten Lauf automatisch die aktuelle Modellaufstellung.

## Modellverfügbarkeit

m3 echo prüft die Verfügbarkeit der KI-Modelle automatisch:

### Vollständig verfügbar
Alle Modelle der aktuellen Aufstellung sind erreichbar und der Test kann wie geplant durchgeführt werden.

### Teilweise nicht verfügbar
Ein oder mehrere Modelle sind temporär nicht erreichbar:

- Der Test kann mit den verbleibenden Modellen durchgeführt werden
- Eine Warnung wird angezeigt, welche Modelle nicht verfügbar sind
- Sie können wählen, ob Sie trotzdem fortfahren oder auf volle Verfügbarkeit warten möchten

### Alle nicht verfügbar
Keines der Modelle der aktuellen Aufstellung ist erreichbar:

- Der Test kann nicht gestartet werden
- Der Administrator prüft die Modellaufstellung unter Admin → Modellpflege
- Warten Sie einige Minuten und versuchen Sie es erneut
- Falls das Problem bestehen bleibt, kontaktieren Sie den Support
`,
  },

  // ───────────────────────────────────────────────
  // 7. Admin-Funktionen
  // ───────────────────────────────────────────────
  {
    id: 'admin',
    title: 'Admin-Funktionen',
    content: `
# Admin-Funktionen

Die Admin-Funktionen sind nur für Benutzer mit der Rolle **Admin** (Platform-Admin) zugänglich. Sie umfassen die Verwaltung von Mandanten, Benutzern, SSO-Konfigurationen, Modellpflege, Kosten und Systemüberwachung.

Wählen Sie einen der folgenden Bereiche:

- **Mandantenverwaltung** — Tenants erstellen und konfigurieren
- **Benutzerverwaltung** — Benutzer anlegen, Rollen zuweisen, Passwörter zurücksetzen
- **SSO-Konfiguration** — Microsoft Entra ID (Azure AD) einrichten
- **Modellpflege** — Modellaufstellung, Kostenvorschau und Websuche-Test
- **Kostenauswertung** — Token-Verbrauch und Kosten überwachen
- **Audit-Log** — Änderungsprotokoll einsehen
- **Systembetrieb** — Watchdog, Alerts und Wartung
`,
    children: [
      // 7.1 Mandantenverwaltung
      {
        id: 'tenant-management',
        title: 'Mandantenverwaltung',
        content: `
# Mandantenverwaltung

## Übersicht

Mandanten (Tenants) sind die oberste Organisationsebene in m3 echo. Jeder Mandant ist eine isolierte Umgebung mit eigenen Benutzern, Projekten und Einstellungen.

## Mandant erstellen

1. Navigieren Sie zu **Admin → Mandanten**
2. Klicken Sie auf **„+ Neuer Mandant"**
3. Füllen Sie die erforderlichen Felder aus:
   - **Name**: Interner, technischer Name (wird als Identifier verwendet)
   - **Display-Name**: Anzeigename, der den Benutzern angezeigt wird
   - **Zeitzone**: Standard-Zeitzone des Mandanten (z. B. Europe/Berlin)

## Mandant bearbeiten

Klicken Sie auf einen bestehenden Mandanten, um die Einstellungen zu bearbeiten:

### Grundeinstellungen

- **Name**: Technischer Name (Änderung mit Vorsicht)
- **Display-Name**: Kann jederzeit angepasst werden
- **Zeitzone**: Beeinflusst Autopilot-Zeitpläne und Anzeigen

### Feature-Flags

Feature-Flags steuern, welche Funktionen für den Mandanten verfügbar sind:

| Feature-Flag | Beschreibung |
|---|---|
| **Benchmark-Tab** | Aktiviert den Benchmark-Vergleich zwischen Projekten |
| **Manuelle LLM Scores** | Erlaubt das manuelle Eintragen von LLM Scores |
| **Manuelle Optimierung** | Erlaubt das manuelle Eintragen von Optimierungswerten |

### Autopilot-Prompt-Kontingent

Legen Sie fest, wie viele Prompt-Ausführungen pro Zeitraum für den Autopilot zur Verfügung stehen:

- **Kontingent-Wert**: Anzahl der erlaubten Prompt-Ausführungen
- Bei Erschöpfung des Kontingents werden keine weiteren Autopilot-Tests ausgeführt
- Das Kontingent wird periodisch zurückgesetzt

### Monatliches Budget

Definieren Sie ein monatliches Budget-Limit für API-Kosten:

- Bei Überschreitung des Budgets können Warnungen oder Sperren konfiguriert werden
- Die aktuellen Kosten werden in der Kostenauswertung angezeigt

## Mandant löschen

1. Klicken Sie auf **„Mandant löschen"**
2. Bestätigen Sie die Löschung im Warndialog

> **Achtung:** Das Löschen eines Mandanten entfernt alle zugehörigen Daten (Benutzer, Projekte, Tests, Ergebnisse) unwiderruflich. Dieser Vorgang kann nicht rückgängig gemacht werden.
`,
      },

      // 7.2 Benutzerverwaltung
      {
        id: 'user-management',
        title: 'Benutzerverwaltung',
        content: `
# Benutzerverwaltung

## Übersicht

Die Benutzerverwaltung ermöglicht das Erstellen, Bearbeiten und Verwalten von Benutzerkonten innerhalb eines Mandanten.

## Benutzer erstellen

1. Navigieren Sie zu **Admin → Benutzer**
2. Klicken Sie auf **„+ Neuer Benutzer"**
3. Füllen Sie die folgenden Felder aus:
   - **E-Mail-Adresse**: Wird als Login-Name verwendet
   - **Vorname** und **Nachname**
   - **Rolle**: Editor oder Betrachter
   - **Temporäres Passwort**: Wird dem Benutzer mitgeteilt

Der neue Benutzer wird mit dem Flag **must_change_password** angelegt. Beim ersten Login muss der Benutzer ein neues Passwort setzen.

## Benutzerrollen

| Rolle | Berechtigungen |
|---|---|
| **Editor** | Kann Projekte erstellen, bearbeiten und löschen. Kann Tests starten und Ergebnisse einsehen. Kann Prompts verwalten und Autopiloten konfigurieren. |
| **Betrachter** | Kann alle Projekte und Ergebnisse **nur lesen**. Keine Bearbeitungs- oder Erstellungsrechte. Ideal für Stakeholder, die Ergebnisse einsehen, aber nicht verändern sollen. |
| **Admin** | Platform-Admin mit vollen Rechten. Kann mandantenübergreifend agieren (cross-tenant). Hat Zugriff auf alle Admin-Funktionen. |

## Passwort-Reset

Administratoren können das Passwort eines Benutzers zurücksetzen:

1. Navigieren Sie zu **Admin → Benutzer**
2. Klicken Sie auf den gewünschten Benutzer
3. Klicken Sie auf **„Passwort zurücksetzen"**
4. Vergeben Sie ein neues temporäres Passwort
5. Der Benutzer muss beim nächsten Login ein neues Passwort setzen

## Benutzer aktivieren / deaktivieren

- **Deaktivieren**: Der Benutzer kann sich nicht mehr anmelden, sein Konto bleibt aber erhalten
- **Aktivieren**: Ein deaktivierter Benutzer kann wieder freigeschaltet werden

Das Deaktivieren ist gegenüber dem Löschen zu bevorzugen, wenn der Benutzer möglicherweise zu einem späteren Zeitpunkt wieder Zugang benötigt oder seine Aktionen im Audit-Log erhalten bleiben sollen.
`,
      },

      // 7.3 SSO-Konfiguration
      {
        id: 'sso-config',
        title: 'SSO-Konfiguration',
        content: `
# SSO-Konfiguration

## Überblick

m3 echo unterstützt **Single Sign-On (SSO)** über **Microsoft Entra ID** (ehemals Azure Active Directory). SSO wird pro Tenant konfiguriert und ermöglicht es Benutzern, sich mit ihren bestehenden Microsoft-Anmeldedaten einzuloggen.

## Konfiguration einrichten

1. Navigieren Sie zu **Admin → SSO**
2. Wählen Sie den gewünschten Tenant
3. Füllen Sie die folgenden Felder aus:

| Feld | Beschreibung | Beispiel |
|---|---|---|
| **Client ID** | Application (Client) ID aus Azure | \`12345678-abcd-efgh-ijkl-123456789012\` |
| **Client Secret** | Client-Geheimnis (write-only, wird verschlüsselt gespeichert) | \`~abc123...\` |
| **Authority URL** | Microsoft Identity Platform Endpoint | \`https://login.microsoftonline.com/{tenant-id}\` |
| **Issuer** | Token-Issuer URL | \`https://login.microsoftonline.com/{tenant-id}/v2.0\` |
| **Erlaubte E-Mail-Domains** | Komma-separierte Liste erlaubter Domains | \`firma.de, firma.com\` |

### Client Secret (Write-Only)

Das Client Secret wird **verschlüsselt gespeichert** und kann nach dem Speichern **nicht mehr ausgelesen** werden. Es wird lediglich angezeigt, ob ein Secret hinterlegt ist oder nicht. Um das Secret zu ändern, geben Sie einfach ein neues ein.

## SSO aktivieren / deaktivieren

- **Aktivieren**: Schalten Sie den SSO-Toggle auf „Aktiv"
- **Deaktivieren**: Schalten Sie den Toggle auf „Inaktiv"

> **Wichtig:** SSO und Passwort-Login können **gleichzeitig** aktiv sein. Benutzer können sich dann wahlweise per SSO oder mit E-Mail/Passwort anmelden. Dies ist besonders nützlich für eine schrittweise Migration zu SSO.

## Voraussetzungen in Microsoft Entra ID

Für die SSO-Konfiguration müssen in Ihrem Azure-Tenant folgende Schritte durchgeführt worden sein:

1. **App-Registrierung** in Microsoft Entra ID erstellen
2. **Redirect URI** konfigurieren: \`https://ihre-domain.de/api/auth/sso/callback\` (genau so auch in der SSO-Konfiguration von m3 echo eintragen)
3. **Client Secret** generieren
4. **API-Berechtigungen**: \`openid\`, \`profile\`, \`email\`
5. **Token-Konfiguration**: Optional Claims für E-Mail-Adresse

## Fehlerbehebung

| Problem | Lösung |
|---|---|
| SSO-Login schlägt fehl | Prüfen Sie Client ID und Authority URL |
| „Domain nicht erlaubt" | Fügen Sie die E-Mail-Domain zur Liste hinzu |
| Token abgelaufen | Prüfen Sie die Token-Lebensdauer in Azure |
| Benutzer wird nicht angelegt | Stellen Sie sicher, dass die E-Mail-Domain erlaubt ist |
`,
      },

      // 7.4 Modellpflege
      {
        id: 'model-management',
        title: 'Modellpflege',
        content: `
# Modellpflege

## Übersicht

Die Modellpflege (Admin → Modellpflege, zuvor „KI-Modelle") ist der zentrale Ort für alle LLM-Modelle. Nutzer wählen keine Modelle aus: Die Modellaufstellung gilt für alle Projekte, Autopiloten, manuellen Tests und Re-Tests (ab dem jeweils nächsten Lauf).

## Modellaufstellung

- Legen Sie fest, welche Modelle in der Aufstellung enthalten sind
- Nur aktive Modelle können Teil der Aufstellung sein
- Neue Modelle werden nach Prüfung in die Aufstellung aufgenommen und stehen dann allen Projekten zur Verfügung
- Autopiloten, manuelle Tests und Re-Tests verwenden die Aufstellung ab dem jeweils nächsten Lauf
- Ergebnisse früherer Modelle bleiben in der Auswertung sichtbar
- Mandanten-Ausschlüsse gelten weiterhin: Ist ein Modell in den Mandanten-Einstellungen für einen Mandanten deaktiviert, wird es für diesen Mandanten auch dann nicht abgefragt, wenn es Teil der Aufstellung ist

## Kostenvorschau

Vor einer Änderung der Aufstellung zeigt die Kostenvorschau, welche Kosten die neue Aufstellung voraussichtlich verursacht. Die Werte sind eine **Schätzung** auf Basis der bisherigen Nutzung und der hinterlegten Preise, keine verbindliche Abrechnung.

## Stabilität

Die Stabilitätsansicht zeigt die Verfügbarkeit der Modelle der letzten 7 Tage und hilft, unzuverlässige Modelle zu erkennen.

## Websuche testen

Mit **Websuche testen** prüfen Sie, ob ein Modell die Websuche (Grounding) korrekt nutzt. Führen Sie den Test durch, bevor Sie ein Modell in die Aufstellung aufnehmen.

Änderungen an der Einstellung **Websuche** (Grounding) eines Modells erreichen die laufenden Worker mit einer Verzögerung von bis zu 5 Minuten.

## Deaktivieren statt Löschen

Modelle, die bereits in Ergebnissen verwendet wurden, können nicht gelöscht werden. Deaktivieren Sie sie stattdessen: Das Modell wird nicht mehr neu verwendet, seine bisherigen Ergebnisse bleiben erhalten.

## Fallback-Modelle der Analyse-Layer

Unter Admin → Layers hat jeder Analyse-Layer neben seinem Modell ein **Fallback-Modell** (oder „Kein Fallback"): Schlägt ein Aufruf mit dem primären Modell fehl, versucht der Layer es einmal mit dem Fallback; ein als Fallback eingetragenes Modell kann nicht gelöscht werden.
`,
      },

      // 7.5 Kostenauswertung
      {
        id: 'cost-reporting',
        title: 'Kostenauswertung',
        content: `
# Kostenauswertung

## Übersicht

Die Kostenauswertung (Cost Reporting) gibt Administratoren einen detaillierten Einblick in den Token-Verbrauch und die daraus resultierenden Kosten.

## Auswertungsdimensionen

Die Kosten können nach folgenden Dimensionen aufgeschlüsselt werden:

### Nach Tenant
- Gesamtverbrauch pro Mandant
- Vergleich zwischen Mandanten
- Budget-Auslastung in Prozent

### Nach Modell
- Token-Verbrauch pro KI-Modell
- Kosten pro Modell (Input-Tokens und Output-Tokens separat)
- Identifikation der kostenintensivsten Modelle

### Nach Zeitraum
- Tages-, Wochen-, Monatsansicht
- Trendanalyse über beliebige Zeiträume
- Vergleich mit Vorperioden

## Metriken

| Metrik | Beschreibung |
|---|---|
| **Input-Tokens** | Anzahl der an das Modell gesendeten Tokens (Prompts) |
| **Output-Tokens** | Anzahl der vom Modell generierten Tokens (Antworten) |
| **Gesamtkosten** | Berechnete Kosten basierend auf Token-Preisen |
| **Budget-Auslastung** | Aktueller Verbrauch im Verhältnis zum monatlichen Budget |

## CSV-Export

Alle Kostenberichte können als **CSV-Datei** exportiert werden:

1. Konfigurieren Sie die gewünschten Filter (Tenant, Modell, Zeitraum)
2. Klicken Sie auf **„CSV Export"**
3. Die Datei wird automatisch heruntergeladen

Die exportierte CSV enthält alle Detaildaten und kann in Excel oder anderen Tabellenprogrammen weiterverarbeitet werden.
`,
      },

      // 7.6 Audit-Log
      {
        id: 'audit-log',
        title: 'Audit-Log',
        content: `
# Audit-Log

## Übersicht

Das Audit-Log protokolliert alle relevanten Aktionen im System. Es beantwortet die Frage: **Wer hat wann was geändert?**

## Protokollierte Aktionen

Das Audit-Log erfasst unter anderem:

- **Benutzer-Aktionen**: Login, Logout, Passwortänderungen
- **Projekt-Aktionen**: Erstellen, Bearbeiten, Löschen von Projekten
- **Test-Aktionen**: Starten und Abschluss von Tests
- **Admin-Aktionen**: Änderungen an Mandanten, Benutzern, SSO, Modellen
- **Autopilot-Aktionen**: Erstellen, Pausieren, Löschen von Autopiloten
- **Systemaktionen**: Konfigurationsänderungen, Wartungsmodus

## Filteroptionen

Das Audit-Log kann nach folgenden Kriterien gefiltert werden:

| Filter | Beschreibung |
|---|---|
| **Benutzer** | Aktionen eines bestimmten Benutzers anzeigen |
| **Aktion** | Nach Aktionstyp filtern (z. B. nur „Projekt erstellt") |
| **Zeitraum** | Von-/Bis-Datum für die Eingrenzung |
| **Tenant** | Aktionen innerhalb eines bestimmten Mandanten (nur für Platform-Admins) |

## CSV-Export

1. Konfigurieren Sie die gewünschten Filter
2. Klicken Sie auf **„CSV Export"**
3. Die Datei enthält alle gefilterten Audit-Einträge mit:
   - Zeitstempel
   - Benutzer (Name und E-Mail)
   - Aktion
   - Betroffenes Objekt
   - Details der Änderung
   - IP-Adresse

## Aufbewahrung

Audit-Log-Einträge werden für **12 Monate** aufbewahrt. Ältere Einträge werden automatisch archiviert.
`,
      },

      // 7.7 Systembetrieb
      {
        id: 'system-ops',
        title: 'Systembetrieb',
        content: `
# Systembetrieb

## Watchdog

Der **Watchdog** überwacht das System auf feststeckende Jobs:

- Erkennt automatisch Jobs, die ungewöhnlich lange im Status „Running" verharren
- Benachrichtigt Administratoren über erkannte Probleme
- Kann feststeckende Jobs automatisch als „Failed" markieren und zur Wiederholung freigeben

### Konfiguration
- **Timeout**: Nach wie vielen Minuten wird ein Job als feststeckend betrachtet (Standard: 10 Minuten)
- **Aktion**: Automatisch fehlschlagen lassen oder nur benachrichtigen

## System-Alerts

Systemweite Warnmeldungen informieren über kritische Zustände:

| Alert-Typ | Beschreibung |
|---|---|
| **API-Fehler** | Ein KI-Modell-Provider meldet wiederholt Fehler |
| **Budget-Warnung** | Ein Tenant nähert sich dem monatlichen Budget-Limit |
| **Kontingent-Warnung** | Das Autopilot-Prompt-Kontingent eines Tenants ist fast aufgebraucht |
| **Watchdog-Alert** | Feststeckende Jobs wurden erkannt |

## Queue-Status

Zeigt den aktuellen Zustand der Job-Warteschlange:

- **Wartende Jobs**: Anzahl der Jobs im Status „Pending"
- **Laufende Jobs**: Anzahl der aktuell ausgeführten Jobs
- **Durchsatz**: Jobs pro Minute/Stunde
- **Durchschnittliche Wartezeit**: Wie lange Jobs in der Warteschlange verbleiben

## Wartungsmodus

Der Wartungsmodus ermöglicht es, das System für geplante Wartungsarbeiten in einen eingeschränkten Zustand zu versetzen:

- **Aktivieren**: Neue Tests werden blockiert, laufende Tests werden abgeschlossen
- **Benutzer-Info**: Angemeldete Benutzer sehen einen Hinweisbanner
- **Deaktivieren**: Normaler Betrieb wird wiederhergestellt

> **Tipp:** Planen Sie Wartungsarbeiten außerhalb der Autopilot-Ausführungszeiten (vermeiden Sie 7:00 Uhr morgens), um keine automatisierten Tests zu unterbrechen.
`,
      },
    ],
  },

  // ───────────────────────────────────────────────
  // 8. Status-Anzeigen
  // ───────────────────────────────────────────────
  {
    id: 'status',
    title: 'Status-Anzeigen',
    content: `
# Status-Anzeigen

## Projekt-Status

Der Projekt-Status gibt einen schnellen Überblick über den Zustand eines Projekts:

- **Aktiv**: Projekt hat mindestens einen abgeschlossenen Test
- **Neu**: Projekt wurde erstellt, aber noch kein Test durchgeführt
- **Autopilot aktiv**: Projekt wird automatisiert überwacht (🔁-Icon im Dashboard)
- **Test läuft**: Ein Test wird gerade für dieses Projekt ausgeführt

## Job-Status

Jeder Job (Test) durchläuft verschiedene Status-Stufen:

### Pending (Wartend)

- **Icon**: Uhr-Symbol ⏱️
- **Bedeutung**: Der Job befindet sich in der Warteschlange und wartet auf Ausführung
- **Typische Dauer**: Wenige Sekunden bis einige Minuten (je nach Auslastung)
- **Aktion**: Abwarten — der Job wird automatisch gestartet, sobald Kapazität verfügbar ist

### Running (Läuft)

- **Icon**: Ladeindikator / Spinner
- **Bedeutung**: Der Job wird aktiv ausgeführt, Prompts werden an KI-Modelle gesendet
- **Typische Dauer**: 30–120 Sekunden
- **Aktion**: Abwarten — Sie können die Seite verlassen, der Job läuft im Hintergrund weiter

### Completed (Abgeschlossen)

- **Icon**: Grüner Haken ✓
- **Bedeutung**: Der Job wurde erfolgreich abgeschlossen
- **Aktion**: Ergebnisse können eingesehen werden

### Failed (Fehlgeschlagen)

- **Icon**: Warnung / Ausrufezeichen ⚠️
- **Bedeutung**: Der Job ist fehlgeschlagen
- **Mögliche Ursachen**: API-Fehler, Timeout, Modell nicht verfügbar
- **Aktion**: Details ansehen, Fehlermeldung prüfen, ggf. erneut versuchen

## Status-Übergänge

Der normale Ablauf ist:

**Pending** → **Running** → **Completed**

Im Fehlerfall:

**Pending** → **Running** → **Failed**

Ein fehlgeschlagener Job kann durch einen manuellen Re-Test oder den Autopilot erneut gestartet werden.
`,
  },

  // ───────────────────────────────────────────────
  // 9. Fehlermeldungen & Lösungen
  // ───────────────────────────────────────────────
  {
    id: 'errors',
    title: 'Fehlermeldungen & Lösungen',
    content: `
# Fehlermeldungen & Lösungen

## Häufige Fehler und deren Behebung

### API Key ungültig

**Fehlermeldung:** „API Key invalid" oder „Authentication failed"

**Ursache:** Der API-Schlüssel für ein KI-Modell ist ungültig, abgelaufen oder nicht konfiguriert.

**Lösung:**
1. Kontaktieren Sie Ihren **Administrator**
2. Der Administrator prüft die API-Key-Konfiguration im Admin-Bereich
3. Gegebenenfalls muss ein neuer API-Key vom Modell-Provider bezogen und hinterlegt werden

---

### Rate Limit erreicht

**Fehlermeldung:** „Rate limit exceeded" oder „Too many requests"

**Ursache:** Zu viele Anfragen wurden in kurzer Zeit an das KI-Modell gesendet. Die Provider begrenzen die Anzahl der Anfragen pro Zeiteinheit.

**Lösung:**
1. **Warten** Sie einige Minuten, bis das Rate Limit zurückgesetzt wird
2. Führen Sie **weniger parallele Tests** durch
3. Verteilen Sie Tests über einen längeren Zeitraum
4. Nutzen Sie den **Autopilot** für eine gleichmäßige Verteilung der Anfragen

---

### Job bleibt auf „Pending"

**Fehlermeldung:** Job verharrt im Status „Pending" mit Uhr-Icon

**Ursache:** Die Warteschlange ist ausgelastet oder es gibt ein technisches Problem mit der Job-Verarbeitung.

**Lösung:**
1. **Warten** Sie einige Minuten — bei hoher Auslastung kann die Wartezeit länger sein
2. **Seite aktualisieren** (F5 oder Browser-Refresh) — der Status wird möglicherweise nicht in Echtzeit aktualisiert
3. Prüfen Sie, ob andere Jobs gerade ausgeführt werden
4. Wenn der Job nach **10+ Minuten** immer noch auf Pending steht, kontaktieren Sie den **Support**

> **Hinweis für Admins:** Der Watchdog erkennt automatisch feststeckende Jobs und kann konfiguriert werden, um sie automatisch zurückzusetzen.

---

### Job fehlgeschlagen (Failed)

**Fehlermeldung:** Job-Status zeigt „Failed" mit Warnsymbol

**Ursache:** Verschiedene Gründe möglich — von temporären API-Fehlern bis hin zu Konfigurationsproblemen.

**Lösung:**
1. **Details ansehen**: Klicken Sie auf den fehlgeschlagenen Job, um die Fehlermeldung einzusehen
2. **Fehlermeldung prüfen**: Die detaillierte Fehlermeldung gibt Hinweise auf die Ursache
3. **Häufige Ursachen:**
   - Modell temporär nicht verfügbar → Erneut versuchen
   - Timeout → Test mit weniger Prompts/Modellen durchführen
   - Ungültige Konfiguration → Projekteinstellungen prüfen
4. **Re-Test**: Starten Sie einen Re-Test, um es mit den gleichen Parametern erneut zu versuchen

---

### Keine Ergebnisse angezeigt

**Fehlermeldung:** Ergebnisseite ist leer nach Testabschluss

**Ursache:** Möglicherweise ein Anzeigefehler oder der Suchbegriff wurde in keiner Antwort gefunden.

**Lösung:**
1. **Seite aktualisieren** (F5)
2. Prüfen Sie, ob der **Suchbegriff korrekt** geschrieben ist
3. Prüfen Sie die **Synonyme** — werden alternative Schreibweisen abgedeckt?
4. Bei anhaltenden Problemen den Support kontaktieren

---

### Zugriff verweigert

**Fehlermeldung:** „Insufficient permissions" oder „Access denied"

**Ursache:** Ihre Benutzerrolle verfügt nicht über die erforderlichen Berechtigungen für die angeforderte Aktion.

**Lösung:**
1. Prüfen Sie Ihre aktuelle **Rolle** (Editor, Betrachter, Admin)
2. Betrachter können keine Projekte erstellen oder Tests starten
3. Kontaktieren Sie Ihren **Administrator**, wenn Sie erweiterte Rechte benötigen
`,
  },

  // ───────────────────────────────────────────────
  // 10. Versionshistorie
  // ───────────────────────────────────────────────
  {
    id: 'changelog',
    title: 'Versionshistorie',
    content: `
# Versionshistorie

## v5.4.0

*Veröffentlicht: Oktober 2026*

### Zentrale Modellaufstellung
- Der Administrator legt in der Modellpflege fest, mit welchen Modellen alle Benchmarks laufen; Nutzer wählen keine Modelle mehr aus
- Tests, Re-Tests und Autopiloten verwenden die Aufstellung ab dem nächsten Lauf; Ergebnisse früherer Modelle bleiben sichtbar
- Kostenvorschau (Schätzung), 7-Tage-Stabilität und „Websuche testen“ in der Modellpflege

### Neue Modelle
- Benchmark: Gemini 3.6 Flash, Gemini 3.1 Pro (Preview), GPT-5.6 Luna
- Analyse-Layer auf Gemini 3, je Layer mit Fallback auf das entsprechende Gemini-2.5-Modell

### LLM Score je Anbieter
- Der LLM Score wird erst je Anbieter gemittelt, dann über die Anbieter: Jeder Anbieter zählt gleich, egal wie viele seiner Modelle in der Aufstellung sind
- Fehlgeschlagene Ergebnisse (API-Fehler, Timeouts, nicht verfügbare Modelle) zählen nicht mehr als 0; ein Test ohne gültiges Ergebnis ist „nicht auswertbar“
- Die Nennungsrate folgt derselben Regel; Projekt- und Mandantenrate sind der Mittelwert der abgeschlossenen Tests
- Alle bisherigen Scores wurden rückwirkend nach der neuen Regel neu berechnet

## v5.2.0

*Veröffentlicht: April 2026*

### Job Integrity Hardening
- Verbesserte **Zuverlässigkeit der Job-Ausführung** durch erweiterte Fehlerbehandlung
- **Automatische Wiederholungsversuche** bei transienten API-Fehlern
- Robustere Handhabung von Netzwerk-Timeouts und unterbrochenen Verbindungen
- Detailliertere **Fehlerprotokolle** für schnellere Diagnose

### Prompt Snapshotting
- Prompts werden bei der Testausführung als **Snapshot gespeichert**
- Nachträgliche Änderungen an Prompts beeinflussen abgeschlossene Tests nicht mehr
- **Vollständige Nachvollziehbarkeit**: Jeder Test enthält den exakten Prompt-Text, der verwendet wurde
- Vergleich zwischen Original-Prompt und Snapshot möglich

### RBAC (Role-Based Access Control)
- **Rollenbasierte Zugriffskontrolle** mit drei Stufen: Editor, Betrachter, Admin
- Granulare Berechtigungen für jede Funktion
- Betrachter-Rolle für **schreibgeschützten Zugang** zu Ergebnissen
- Admin-Rolle mit **mandantenübergreifenden** Berechtigungen

### SSO (Single Sign-On)
- **Microsoft Entra ID** (Azure AD) Integration
- Pro-Tenant-Konfiguration mit Client ID, Client Secret und Authority URL
- **Gleichzeitige Nutzung** von SSO und Passwort-Login möglich
- Automatische Benutzeranlage bei erstem SSO-Login (wenn Domain erlaubt)

### Audit Log
- **Lückenloses Änderungsprotokoll** aller relevanten Aktionen
- Filter nach Benutzer, Aktion, Zeitraum und Tenant
- **CSV-Export** für Compliance-Anforderungen
- 12 Monate Aufbewahrungsdauer

### Monitoring & Alerting
- **Watchdog** für feststeckende Jobs mit konfigurierbarem Timeout
- **System-Alerts** für API-Fehler, Budget-Warnungen und Queue-Probleme
- **Queue-Status-Übersicht** mit Durchsatz-Metriken
- **Wartungsmodus** für geplante Systemarbeiten

### Onboarding
- Verbesserter **5-Schritt-Wizard** für die Projekterstellung
- Hilfreiche Tooltips und Erklärungen in jedem Schritt
- **Validierung** in Echtzeit mit sofortigem Feedback
- Optimierte mobile Darstellung des Wizards

---

## v5.1.0

*Veröffentlicht: März 2026*

### Scoring V2 — 7 AI-Visibility-Kategorien
- Neues **7-Kategorien-Bewertungssystem** für die Optimierungsanalyse
- **Entity-Klarheit** (K1, 20 %), **Zitierfähigkeit** (K2, 20 %), **Reputation** (K3, 20 %)
- **USP** (K4, 12 %), **Lokale Relevanz** (K5, 10 %), **Drittquellen** (K6, 13 %), **Technische Signale** (K7, 5 %)
- Detaillierte **Sub-Signale** pro Kategorie mit individuellen Empfehlungen
- Fünfstufige Bewertungsskala: Exzellent, Gut, Ausbaufähig, Schwach, Kritisch

### Billing-Modul
- **Token-Verbrauchstracking** nach Tenant, Modell und Zeitraum
- **Monatliche Budget-Limits** pro Tenant
- **CSV-Export** der Kostenberichte
- Automatische **Budget-Warnungen** bei Annäherung an Limits

### Combined Timeline
- **Unified View** von LLM Score und Optimierungsscore in einer Zeitlinie
- **Pearson-Korrelationskoeffizient** (r) zur Messung des Zusammenhangs
- **Smart Grouping** für bessere Übersichtlichkeit bei vielen Datenpunkten
- Interaktives Diagramm mit Tooltips und Zoom-Funktionalität
`,
  },
];

/**
 * Hilfsfunktion: Alle Sections (inkl. children) als flache Liste zurückgeben
 */
export function flattenSections(sections: WikiSection[]): WikiSection[] {
  const result: WikiSection[] = [];
  for (const section of sections) {
    result.push(section);
    if (section.children) {
      result.push(...flattenSections(section.children));
    }
  }
  return result;
}

/**
 * Hilfsfunktion: Section anhand der ID finden
 */
export function findSectionById(
  sections: WikiSection[],
  id: string
): WikiSection | undefined {
  for (const section of sections) {
    if (section.id === id) return section;
    if (section.children) {
      const found = findSectionById(section.children, id);
      if (found) return found;
    }
  }
  return undefined;
}
